import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  ArrowRight,
  CheckCircle2,
  Clock,
  ShieldCheck,
  Sparkles,
  Quote,
  ChevronLeft,
} from "lucide-react";

import { fetchChecker } from "@/lib/api";
import { getCheckerIcon, SEVERITY } from "@/lib/checker-icons";
import TrustBadges from "@/components/TrustBadges";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

export default function CheckerLanding() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [checker, setChecker] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    setChecker(null);
    fetchChecker(slug)
      .then(setChecker)
      .catch(() => setError("Deze checker is niet gevonden."));
    window.scrollTo(0, 0);
  }, [slug]);

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center">
        <h1 className="font-display text-3xl font-bold text-ink">Checker niet gevonden</h1>
        <p className="text-ink-soft mt-2">{error}</p>
        <Link to="/" className="inline-block mt-6 text-[#002FA7] font-semibold">
          ← Terug naar overzicht
        </Link>
      </div>
    );
  }

  if (!checker) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32">
        <div className="h-8 w-2/3 bg-slate-200 rounded animate-pulse" />
        <div className="h-4 w-1/2 bg-slate-200 rounded animate-pulse mt-4" />
      </div>
    );
  }

  const Icon = getCheckerIcon(checker.icon);
  const accent = checker.category_color;

  const FAQ_ITEMS = [
    {
      q: `Voor welk type document is ${checker.name} bedoeld?`,
      a: `${checker.name} is gespecialiseerd in ${checker.tagline.toLowerCase()} ${checker.hero_subtitle}`,
    },
    {
      q: "Welke wetgeving gebruiken jullie?",
      a: "We werken met de actuele Belgische en Nederlandse wetgeving. Per bevinding zie je de wettelijke referentie zodat je weet waarop het advies gebaseerd is.",
    },
    {
      q: "Wat als mijn document onleesbaar is?",
      a: "Probeer een betere scan of foto, of plak de tekst rechtstreeks. We geven aan wanneer de inhoud te beperkt was om een grondige analyse te doen.",
    },
    {
      q: "Hoe lang duurt het?",
      a: "Gemiddeld 30 tot 90 seconden, afhankelijk van de grootte van het document.",
    },
  ];

  return (
    <div data-testid={`checker-landing-${checker.slug}`}>
      <Helmet>
        <title>{`${checker.name} — ${checker.tagline} | CheckExpert`}</title>
        <meta name="description" content={checker.hero_subtitle} />
        <meta property="og:title" content={`${checker.name} — CheckExpert`} />
        <meta property="og:description" content={checker.tagline} />
        <meta property="og:type" content="website" />
        <link rel="canonical" href={`https://checkexpert.be/check/${checker.slug}`} />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          "name": checker.name,
          "description": checker.hero_subtitle,
          "provider": { "@type": "Organization", "name": "CheckExpert", "url": "https://checkexpert.be" },
          "areaServed": ["BE", "NL"],
          "offers": { "@type": "Offer", "price": "4.99", "priceCurrency": "EUR" },
        })}</script>
      </Helmet>
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-border">
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            background: `radial-gradient(circle at 30% 30%, ${accent} 0%, transparent 60%)`,
          }}
        />
        <div className="relative max-w-7xl mx-auto px-6 pt-10 pb-20">
          <Link
            to="/"
            className="inline-flex items-center gap-1 text-sm text-ink-soft hover:text-ink mb-8"
            data-testid="back-to-home"
          >
            <ChevronLeft className="h-4 w-4" /> Alle checks
          </Link>

          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <div className="flex items-center gap-3 mb-6">
                <div
                  className="h-14 w-14 rounded-2xl flex items-center justify-center text-white shadow-lg"
                  style={{ background: accent }}
                >
                  <Icon className="h-7 w-7" />
                </div>
                <span
                  className="text-xs uppercase tracking-wider font-semibold px-3 py-1.5 rounded-full"
                  style={{ background: `${accent}1A`, color: accent }}
                >
                  {checker.category_label}
                </span>
              </div>
              <h1 className="font-display text-5xl sm:text-6xl font-bold tracking-tight text-ink leading-[1.05]">
                {checker.name}
              </h1>
              <p className="font-display text-2xl text-ink-soft mt-3 leading-snug">
                {checker.tagline}
              </p>
              <p className="text-lg text-ink-soft mt-6 max-w-xl leading-relaxed">
                {checker.hero_subtitle}
              </p>

              <div className="mt-8 flex flex-wrap items-center gap-4">
                <button
                  onClick={() => navigate(`/check/${slug}/analyse`)}
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full text-white font-semibold shadow-lg transition-transform hover:-translate-y-0.5"
                  style={{ background: accent, boxShadow: `0 10px 30px ${accent}33` }}
                  data-testid="checker-hero-cta"
                >
                  Start mijn {checker.name} — €4,99 <ArrowRight className="h-4 w-4" />
                </button>
                <div className="flex items-center gap-2 text-sm text-ink-soft">
                  <Clock className="h-4 w-4" /> Resultaat in 60 sec
                </div>
              </div>

              <div className="mt-8 flex flex-wrap items-center gap-4 text-xs text-ink-soft">
                <span className="flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-[#10B981]" /> GDPR-conform</span>
                <span className="flex items-center gap-1.5"><Sparkles className="h-4 w-4 text-[#002FA7]" /> Gespecialiseerd in {checker.category_label.toLowerCase()}</span>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="rounded-3xl border border-border bg-white p-8 shadow-xl">
                <p className="text-xs uppercase tracking-wider text-ink-soft font-semibold">
                  Ideaal voor
                </p>
                <ul className="mt-3 space-y-2">
                  {checker.ideal_for.map((p) => (
                    <li key={p} className="flex items-start gap-2 text-ink">
                      <CheckCircle2 className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: accent }} />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-6 pt-6 border-t border-border">
                  <p className="text-xs uppercase tracking-wider text-ink-soft font-semibold">
                    Voorbeelden van wat we ontdekken
                  </p>
                  <ul className="mt-3 space-y-3">
                    {checker.sample_findings.map((f, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-ink-soft">
                        <span
                          className="severity-dot mt-1.5"
                          style={{ background: accent }}
                        />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-end mb-12">
            <div>
              <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: accent }}>
                Hoe het werkt
              </span>
              <h2 className="font-display text-4xl sm:text-5xl font-bold text-ink mt-3 tracking-tight">
                Van twijfel naar zekerheid in 60 seconden.
              </h2>
            </div>
            <p className="text-ink-soft text-lg max-w-md">
              Geen ingewikkelde taal. Per bevinding krijg je een concrete actie en, waar
              relevant, de wettelijke onderbouwing.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              { step: "01", title: "Upload of plak je document", text: "PDF, Word (.docx), foto of getypte tekst." },
              { step: "02", title: "Wij analyseren je document", text: "Specifiek gericht op jouw type document, BE/NL wetgeving en markttarieven." },
              { step: "03", title: "Helder rapport met acties", text: "Score, bevindingen per severity, concrete vervolgstappen." },
            ].map((s) => (
              <div key={s.step} className="rounded-2xl bg-secondary border border-border p-8">
                <div className="font-display text-5xl font-black" style={{ color: `${accent}26` }}>
                  {s.step}
                </div>
                <h3 className="font-display text-xl font-semibold text-ink mt-3">
                  {s.title}
                </h3>
                <p className="text-sm text-ink-soft mt-2 leading-relaxed">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SAMPLE REPORT */}
      <section className="py-24 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <span className="text-xs font-semibold uppercase tracking-wider text-blue-300">
              Voorbeeld rapport
            </span>
            <h2 className="font-display text-4xl font-bold tracking-tight mt-3">
              Zo ziet jouw {checker.name}-rapport eruit.
            </h2>
            <p className="text-slate-300 mt-6 leading-relaxed">
              Een overzichtelijke score, alle bevindingen geclassificeerd op
              ernst, en voor elke bevinding een concrete actie.
            </p>
            <button
              onClick={() => navigate(`/check/${slug}/analyse`)}
              className="mt-8 inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-white text-ink font-semibold hover:bg-blue-50 transition-colors"
            >
              Begin mijn analyse <ArrowRight className="h-4 w-4" />
            </button>
          </div>
          <div className="lg:col-span-7">
            <div className="rounded-3xl bg-white text-ink p-6 shadow-2xl">
              <div className="flex items-center justify-between p-2">
                <div>
                  <p className="text-xs uppercase tracking-wider text-ink-soft">
                    {checker.name}
                  </p>
                  <h3 className="font-display text-xl font-bold text-ink mt-1">
                    Voorbeeld bevindingen
                  </h3>
                </div>
                <div className="text-right">
                  <div className="font-display text-4xl font-bold" style={{ color: accent }}>
                    68
                  </div>
                  <div className="text-xs text-ink-soft">van 100</div>
                </div>
              </div>
              <div className="mt-4 space-y-2">
                {checker.sample_findings.map((f, i) => {
                  const sevKey = ["critical", "high", "medium", "low", "ok"][i % 5];
                  const sev = SEVERITY[sevKey];
                  return (
                    <div
                      key={i}
                      className="flex items-center justify-between p-3 rounded-xl bg-slate-50 gap-3"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="h-2.5 w-2.5 rounded-full flex-shrink-0" style={{ background: sev.color }} />
                        <span className="text-sm font-medium truncate">{f}</span>
                      </div>
                      <span
                        className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full flex-shrink-0"
                        style={{ background: sev.bg, color: sev.text }}
                      >
                        {sev.label}
                      </span>
                    </div>
                  );
                })}
              </div>
              <p className="text-xs text-ink-subtle mt-4 italic">
                Voorbeeldbevindingen. Jouw rapport is gebaseerd op jouw document.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST */}
      <section className="py-24 bg-white border-y border-border">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: accent }}>
              Vertrouwen
            </span>
            <h2 className="font-display text-4xl font-bold text-ink mt-3 tracking-tight">
              Waarom je deze check kan vertrouwen.
            </h2>
          </div>
          <TrustBadges />
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-10">
            <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: accent }}>
              FAQ
            </span>
            <h2 className="font-display text-4xl font-bold text-ink mt-3 tracking-tight">
              Vragen over {checker.name}.
            </h2>
          </div>
          <Accordion type="single" collapsible className="space-y-3">
            {FAQ_ITEMS.map((item, i) => (
              <AccordionItem
                key={i}
                value={`faq-${i}`}
                className="bg-white border border-border rounded-2xl px-6"
              >
                <AccordionTrigger className="font-display text-lg font-semibold text-ink hover:no-underline py-5">
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="text-ink-soft pb-5 leading-relaxed">
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA BOTTOM */}
      <section className="pb-24">
        <div className="max-w-5xl mx-auto px-6">
          <div
            className="rounded-3xl p-12 text-center text-white"
            style={{
              background: `linear-gradient(135deg, ${accent} 0%, ${accent}CC 100%)`,
            }}
          >
            <Quote className="h-8 w-8 mx-auto opacity-80" />
            <h3 className="font-display text-3xl sm:text-4xl font-bold mt-4 tracking-tight">
              Klaar om je document te laten checken?
            </h3>
            <p className="opacity-90 mt-3 max-w-xl mx-auto">
              60 seconden later weet je exact waar de risico's zitten en wat je
              kan ondernemen.
            </p>
            <button
              onClick={() => navigate(`/check/${slug}/analyse`)}
              className="mt-7 inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-white text-ink font-semibold hover:bg-slate-50 transition-colors"
              data-testid="checker-bottom-cta"
            >
              Start {checker.name} <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
